import { useState, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { FormattedMessage, useIntl } from "react-intl";
import { formatServiceError } from "../../../Utilities/serviceErrorHelper";

export default function useFocusControl({
  settingKey,
  entityId,
  entityIdKey,
  focusControlSettings,
  saveFn,
  deleteFn,
  onRefreshSettings,
  buildDeviantData,
}) {
  const intl = useIntl();
  const [savingTarget, setSavingTarget] = useState(null);
  const [deletingTarget, setDeletingTarget] = useState(null);

  const parseTargetSettings = useCallback(
    (fcSettings, target) => {
      const item = fcSettings.find((x) => x[settingKey]?.target === target);
      return {
        type:
          item?.[settingKey]?.focusControlSetting?.type ??
          (target === 0 ? 0 : 1),
        inherited: item
          ? (item.isInherited ?? false)
          : settingKey !== "programSetting",
        delaySeconds:
          item?.[settingKey]?.focusControlSetting?.delaySeconds ?? [],
        maxCount: item?.[settingKey]?.focusControlSetting?.maxCount,
      };
    },
    [settingKey],
  );

  const settings = useMemo(
    () => focusControlSettings || [],
    [focusControlSettings],
  );
  const targetSettings = {
    moduleTest: parseTargetSettings(settings, 0),
    finalExam: parseTargetSettings(settings, 1),
  };

  const { deviantClasses = [], deviantStudents = [] } = useMemo(
    () => buildDeviantData?.(settings) ?? {},
    [buildDeviantData, settings],
  );

  const handleSaveFocusTarget = async (target, newType, newMaxCount) => {
    if (target !== 0 && target !== 1) return;

    setSavingTarget(target);

    try {
      const currentSettings =
        target === 0 ? targetSettings.moduleTest : targetSettings.finalExam;
      const targetDelaySeconds = currentSettings.delaySeconds;
      const targetMaxCount =
        newType === 3
          ? (newMaxCount ?? currentSettings.maxCount)
          : currentSettings.maxCount;

      await saveFn({
        [entityIdKey]: entityId,
        target,
        type: newType,
        delaySeconds: targetDelaySeconds,
        maxCount: targetMaxCount,
      });

      toast.success(<FormattedMessage id="FocusUpdated" />);
    } catch (err) {
      toast.warn(formatServiceError(intl, err?.response?.data));
    } finally {
      await onRefreshSettings?.();
      setSavingTarget(null);
    }
  };

  const handleDeleteFocusControl = deleteFn
    ? async (target) => {
        setDeletingTarget(target);
        try {
          await deleteFn({
            [entityIdKey]: entityId,
            target,
          });
          toast.success(<FormattedMessage id="FocusUpdated" />);
        } catch (err) {
          toast.warn(formatServiceError(intl, err?.response?.data));
        } finally {
          await onRefreshSettings?.();
          setDeletingTarget(null);
        }
      }
    : undefined;

  return {
    targetSettings,
    deviantClasses,
    deviantStudents,
    savingTarget,
    deletingTarget,
    handleSaveFocusTarget,
    handleDeleteFocusControl,
  };
}
